import { effect, inject, Injectable, Type, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { map } from 'rxjs/operators';

import { AppRouteData } from '@app/app-routing.interface';
import { ProjectState } from '@app/projects/project.state';
import { ModalRef } from '@app/shared/app-modal.service';
import { Modal } from '@app/shared/modal/modal.model';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private readonly route = inject(ActivatedRoute);
  private readonly modalService = inject(NgbModal);
  private readonly router = inject(Router);
  private readonly projectState = inject(ProjectState);
  private readonly modalParam = toSignal(
    this.route.queryParams.pipe(map((params) => params['modal'])),
  );

  private modalsMap = new Map<Modal, ModalRef<unknown>>();

  constructor() {
    effect(() => {
      const modalId = this.modalParam();
      untracked(() => {
        if (modalId) {
          this.openModal(modalId);
        } else {
          this.closeAllModals();
        }
      });
    });
  }

  private async openModal(modalId: Modal): Promise<void> {
    if (this.modalsMap.has(modalId)) {
      return;
    }
    const modal = await this.getComponentForModal(modalId);
    if (!modal) {
      return;
    }
    const modalRef = this.modalService.open(modal.component, modal.options ?? { size: 'lg' });

    if (modal.configure) {
      modal.configure(modalRef.componentInstance);
    }

    this.modalsMap.set(modalId, modalRef);

    modalRef.result.then(
      () => this.modalClosedHandler(modalId),
      () => this.modalClosedHandler(modalId),
    );
  }

  private closeAllModals(): void {
    this.modalsMap.forEach((modalRef) => {
      modalRef.close();
    });
    this.modalsMap.clear();
  }

  private modalClosedHandler(modalId: Modal): void {
    this.modalsMap.delete(modalId);
    if (this.modalParam()) {
      const data: AppRouteData = this.route.data;
      this.router.navigate([], {
        queryParams: data.minimalQueryParamsChange
          ? { modal: null, offcanvas: null }
          : { jobId: null, modal: null, offcanvas: null, robotId: null },
      });
    }
  }

  private createModalConfig<T>(
    component: Type<T>,
    options?: NgbModalOptions,
    configure?: (instance: T) => void,
  ): { component: Type<T>; options?: NgbModalOptions; configure?: (instance: unknown) => void } {
    return {
      component,
      options,
      configure: configure as undefined | ((instance: unknown) => void),
    };
  }

  private async getComponentForModal(modalId: Modal): Promise<null | {
    component: unknown;
    options?: NgbModalOptions;
    configure?: (instance: unknown) => void;
  }> {
    let component: unknown;
    switch (modalId) {
      case Modal.ClpDocs:
        ({ RfwClpDocsComponent: component } =
          await import('@app/shared/rfw-clp/rfw-clp-docs.component'));
        return { component, options: { size: 'xl' } };
      case Modal.NewDataTable:
        ({ NewDataTableModalComponent: component } =
          await import('@app/3.0/data-tables/new-data-table-modal/new-data-table-modal.component'));
        return { component };
      case Modal.NewRobot:
        ({ NewRobotComponent: component } = await import('@app/3.0/new-robot/new-robot.component'));
        return { component };
      case Modal.NewProject:
        ({ NewProjectModalComponent: component } =
          await import('@app/projects/new-project-modal.component'));
        return { component };
      case Modal.NewJob:
        ({ NewJobModalComponent: component } =
          await import('@app/3.0/jobs/job-modal/job-modal.component'));
        return {
          component,
        };
      case Modal.RunJob:
        ({ RunJobComponent: component } = await import('@app/3.0/jobs/run-job/run-job.component'));
        return {
          component,
        };
      case Modal.QuickStart:
        ({ QuickstartModalComponent: component } =
          await import('@app/quickstart/quickstart-modal.component'));
        return {
          component,
          options: {
            backdrop: undefined,
          },
        };
      case Modal.ChangeLanguage:
        ({ ChangeLanguageModalComponent: component } =
          await import('@app/shared/translations/change-language-modal/change-language-modal.component'));
        return {
          component,
          options: {
            size: 'md',
          },
        };
      case Modal.UploadDataFile:
        ({ UploadDataTableModalComponent: component } =
          await import('@app/3.0/data-tables/upload-data-table-modal/upload-data-table-modal.component'));
        return { component };
      default:
        return null;
    }
  }
}
